import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AgentAvatar from "../AgentAvatar";

const SaveButton = styled(Button)(({ theme }) => ({
  borderRadius: "6px",
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  padding: "6px 16px",
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.text.secondary,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
    opacity: 0.4,
  },
}));

const NavItem = styled(Box, { shouldForwardProp: (p) => p !== "active" })(
  ({ active, theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: active ? 600 : 400,
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    backgroundColor: active ? theme.palette.action.hover : "transparent",
    transition: "all 0.15s",
    userSelect: "none",
    "&:hover": {
      backgroundColor: active
        ? theme.palette.action.hover
        : theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  })
);

export default function AgentConfigDrawerShell({
  open,
  onClose,
  saving,
  isCreateMode,
  loaded,
  showCreationPathChooser,
  showManualConfigurationLayout,
  isEditLoading,
  headerTitle,
  headerSubtitle,
  avatarName,
  isSystemAgent,
  saveErrorMessage,
  handleSave,
  saveButtonDisabled,
  saveResult,
  saveButtonLabel,
  renderCreationPathChooser,
  resetCreationPathSelection,
  activeTab,
  setActiveTab,
  navTabs,
  renderGeneralTab,
  renderAccessTab,
  renderChatUxTab,
  renderToolsTab,
  renderKnowledgeTab,
  renderEmailTab,
  renderDangerTab,
  renderAiCreationPanel,
}) {
  return (
    <Dialog
      open={open}
      onClose={saving ? () => {} : onClose}
      disableEscapeKeyDown={saving}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          border: 1,
          borderColor: "divider",
          maxHeight: "85vh",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "85vh" }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <AgentAvatar
            name={avatarName}
            size={36}
            borderRadius="8px"
            systemAgent={isSystemAgent}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 600, color: "text.primary" }}
            >
              {headerTitle}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "text.disabled" }}>
              {headerSubtitle}
            </Typography>
          </Box>
          {!showCreationPathChooser && !isEditLoading && (
            <SaveButton
              onClick={handleSave}
              disabled={saveButtonDisabled}
              sx={
                saveResult === "success"
                  ? {
                      backgroundColor: "success.main",
                      "&.Mui-disabled": {
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                        opacity: 1,
                      },
                    }
                  : saveResult === "error"
                  ? {
                      backgroundColor: "error.main",
                      "&.Mui-disabled": {
                        backgroundColor: "error.main",
                        color: "error.contrastText",
                        opacity: 1,
                      },
                    }
                  : {}
              }
            >
              {saveButtonLabel}
            </SaveButton>
          )}
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {saveErrorMessage && (
          <Box
            sx={{
              mx: 3,
              mt: 2,
              mb: 0.5,
              px: 1.5,
              py: 1.25,
              borderRadius: "8px",
              border: 1,
              borderColor: "error.light",
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.05),
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "error.dark",
                mb: 0.25,
              }}
            >
              Save failed
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "error.dark", lineHeight: 1.35 }}
            >
              {saveErrorMessage}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          {!isCreateMode && !loaded ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : showCreationPathChooser ? (
            renderCreationPathChooser()
          ) : showManualConfigurationLayout ? (
            <>
              <Box
                sx={{
                  width: 180,
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: "divider",
                  py: 2,
                  px: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  backgroundColor: "action.hover",
                }}
              >
                {isCreateMode && (
                  <Button
                    size="small"
                    variant="text"
                    disabled={saving}
                    onClick={resetCreationPathSelection}
                    sx={{
                      mb: 1,
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontSize: "12px",
                      color: "text.secondary",
                      px: 1,
                    }}
                  >
                    Change setup mode
                  </Button>
                )}
                {navTabs
                  .filter((tab) => {
                    if (tab.danger && isCreateMode) return false;
                    if (isSystemAgent && tab.id !== "general") return false;
                    return true;
                  })
                  .map((tab) => (
                    <NavItem
                      key={tab.id}
                      active={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <tab.icon
                        sx={{
                          fontSize: 18,
                          color: tab.danger ? "error.main" : undefined,
                        }}
                      />
                      <span
                        style={{
                          color:
                            tab.danger && activeTab === tab.id
                              ? "var(--mui-palette-error-main)"
                              : undefined,
                        }}
                      >
                        {tab.label}
                      </span>
                    </NavItem>
                  ))}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: (theme) =>
                    `${theme.palette.divider} transparent`,
                }}
              >
                <Box sx={{ py: 3, px: 3 }}>
                  {activeTab === "general" && renderGeneralTab()}
                  {activeTab === "access" && renderAccessTab()}
                  {activeTab === "chatUx" && renderChatUxTab()}
                  {activeTab === "tools" && renderToolsTab()}
                  {activeTab === "knowledge" && renderKnowledgeTab()}
                  {activeTab === "email" && renderEmailTab()}
                  {activeTab === "danger" && renderDangerTab()}
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  width: 180,
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: "divider",
                  py: 2,
                  px: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "action.hover",
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  disabled={saving}
                  onClick={resetCreationPathSelection}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: "12px",
                    color: "text.secondary",
                    px: 1,
                  }}
                >
                  Change setup mode
                </Button>
              </Box>
              {renderAiCreationPanel()}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
